import FormattedMessage from 'I18n/components/FormattedMessage';
import { BookIcon } from 'foundations-assets/icons/components/BookIcon';
import { LessonIcon } from 'foundations-assets/icons/components/LessonIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { BreakString } from 'foundations-components/transitional/components/BreakString';
import { Button } from 'foundations-components/transitional/components/Button';
import { ExpandableText } from 'foundations-components/transitional/components/ExpandableText';
import { useCallback, useMemo } from 'react';
import SanitizedText from 'sanitize-text/components/SanitizedText';
import { useDetailsPageContext } from '../../context/DetailsPageContext';
import { MediaCarousel } from '../../media/MediaCarousel';
import { trackCustomMarketplaceOfferingBehavioralEvent } from '../../utils/TrackingUtils';
import { DetailsPageCard } from './DetailsPageCard';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function ProductOverviewCard({
  caseStudyUrl,
  overview,
  screenshots,
  setupDocsUrl,
  videos
}) {
  const {
    runLeadGen,
    trackInteraction
  } = useDetailsPageContext();
  const handleTrackInteraction = useCallback(eventProps => {
    trackInteraction(Object.assign({
      section: 'product overview card'
    }, eventProps));
  }, [trackInteraction]);
  const mediaCarouselItems = useMemo(() => {
    const videoCarouselItems = (videos === null || videos === void 0 ? void 0 : videos.map(v => {
      var _v$demoVideoThumbnail;
      return {
        type: 'video',
        source: v.video.value,
        alt: v.video.altText || '',
        thumbnailUrl: (_v$demoVideoThumbnail = v.demoVideoThumbnail) === null || _v$demoVideoThumbnail === void 0 ? void 0 : _v$demoVideoThumbnail.value
      };
    })) || [];
    const screenshotCarouselItems = screenshots.map(s => ({
      type: 'image',
      source: s.screenshot.value,
      alt: s.screenshot.altText ? s.screenshot.altText : ''
    }));
    return [...videoCarouselItems, ...screenshotCarouselItems];
  }, [screenshots, videos]);
  const showButtonBar = useMemo(() => {
    return caseStudyUrl || setupDocsUrl;
  }, [caseStudyUrl, setupDocsUrl]);
  return /*#__PURE__*/_jsx(DetailsPageCard, {
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.overviewCard.title"
    }),
    children: /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "400",
      children: [/*#__PURE__*/_jsx(MediaCarousel, {
        onTrackInteraction: handleTrackInteraction,
        items: mediaCarouselItems,
        onItemClick: () => {
          handleTrackInteraction({
            action: 'click carousel image'
          });
          runLeadGen({
            action: 'CLICKED_IMAGE'
          });
        }
      }), /*#__PURE__*/_jsx(Flex, {
        "data-test-id": "expandable-overview",
        children: /*#__PURE__*/_jsx(ExpandableText, {
          maxHeight: 280,
          hideButtonCaret: true,
          buttonAlign: "left",
          expandButtonText: /*#__PURE__*/_jsx(Text, {
            as: "span",
            color: "text-interactive-default",
            textDecoration: "underline",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.overviewCard.expandButtonText"
            })
          }),
          collapseButtonText: /*#__PURE__*/_jsx(Text, {
            as: "span",
            color: "text-interactive-default",
            textDecoration: "underline",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.overviewCard.collapseButtonText"
            })
          }),
          children: /*#__PURE__*/_jsx(BreakString, {
            children: /*#__PURE__*/_jsx(SanitizedText, {
              body: overview
            })
          })
        })
      }), showButtonBar && /*#__PURE__*/_jsxs(Flex, {
        children: [caseStudyUrl && /*#__PURE__*/_jsxs(Button, {
          external: true,
          href: caseStudyUrl,
          onClick: () => {
            handleTrackInteraction({
              action: 'click case study'
            });
          },
          size: "xs",
          use: "tertiary-light",
          children: [/*#__PURE__*/_jsx(BookIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.overviewCard.caseStudy"
          })]
        }), setupDocsUrl && /*#__PURE__*/_jsxs(Button, {
          external: true,
          href: setupDocsUrl,
          onClick: () => {
            handleTrackInteraction({
              action: 'click setup guide'
            });
            trackCustomMarketplaceOfferingBehavioralEvent('SIDEBAR_SETUP_GUIDE');
            runLeadGen({
              action: 'CLICKED_SETUP_GUIDE'
            });
          },
          size: "xs",
          use: "tertiary-light",
          children: [/*#__PURE__*/_jsx(LessonIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.overviewCard.setupGuide"
          })]
        })]
      })]
    })
  });
}